/**
 * SalesDashboardPage — Phase 33.9 entry point.
 *
 * Summary tiles, a "Needs Action" banner for new uncontacted leads,
 * and a recent-leads feed. Polls every 30 s.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface LeadSummary {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  company: string;
  company_size_estimate: number | null;
  industry: string;
  linkedin_url: string | null;
  email: string | null;
  discovery_source: string;
  discovery_signal: string;
  discovered_at: number;
  pipeline_id: string | null;
  status: string;
}

interface MetricsResponse {
  total_pipelines: number;
  running_pipelines: number;
  completed_pipelines: number;
  failed_pipelines: number;
  total_leads_found: number;
  total_leads_qualified: number;
  total_emails_drafted: number;
  total_meetings_booked: number;
  conversion_leads_to_qualified: number;
  conversion_qualified_to_email: number;
}

interface LeadsResponse {
  leads: LeadSummary[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchLeads(): Promise<LeadsResponse> {
  return apiFetch<LeadsResponse>('/sales/leads');
}

function fetchMetrics(): Promise<MetricsResponse> {
  return apiFetch<MetricsResponse>('/sales/pipeline/metrics');
}

function useLeads() {
  return useQuery({
    queryKey: ['sales', 'leads'],
    queryFn: fetchLeads,
    refetchInterval: 30_000,
  });
}

function useMetrics() {
  return useQuery({
    queryKey: ['sales', 'pipeline', 'metrics'],
    queryFn: fetchMetrics,
    refetchInterval: 30_000,
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  new:        '#7dd3fc',
  contacted:  '#a78bfa',
  qualified:  '#6ee7b7',
  proposed:   '#fbbf24',
  won:        '#34d399',
  lost:       '#f87171',
};

export function statusColor(s: string): string {
  return STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, color, highlight }: {
  label: string;
  value: string | number;
  color?: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: highlight
          ? 'rgba(125,211,252,0.08)'
          : 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: highlight
          ? '1px solid rgba(125,211,252,0.4)'
          : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 18px',
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>
        {value}
      </div>
    </div>
  );
}

function NeedsActionBanner({ leads }: { leads: LeadSummary[] }) {
  if (leads.length === 0) return null;
  return (
    <div
      style={{
        background: 'rgba(125,211,252,0.07)',
        border: '1px solid rgba(125,211,252,0.35)',
        borderRadius: 6,
        padding: '14px 18px',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#7dd3fc',
            boxShadow: '0 0 6px 2px rgba(125,211,252,0.5)',
          }}
        />
        <span style={{ fontSize: '9.5pt', fontWeight: 700, color: '#7dd3fc', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {leads.length} New Lead{leads.length !== 1 ? 's' : ''} Not Yet Contacted
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {leads.map(l => (
          <Link
            key={l.id}
            to={`/omega/sales/leads/${l.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 10px',
              background: 'rgba(125,211,252,0.06)',
              border: '1px solid rgba(125,211,252,0.18)',
              borderRadius: 4,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span style={{ flex: 1, fontSize: '9.5pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.first_name} {l.last_name} — {l.company}
            </span>
            <span style={{ flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
              {fmtTime(l.discovered_at)}
            </span>
            <span style={{ flexShrink: 0, fontSize: '8.5pt', fontWeight: 700, color: '#7dd3fc', letterSpacing: '0.04em' }}>
              Contact →
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RecentRow({ l }: { l: LeadSummary }) {
  return (
    <Link
      to={`/omega/sales/leads/${l.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: '7.5pt',
          padding: '1px 6px',
          border: `1px solid ${statusColor(l.status)}`,
          color: statusColor(l.status),
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 700,
          borderRadius: 3,
        }}
      >
        {l.status}
      </span>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontSize: '9.5pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {l.first_name} {l.last_name}
          {l.title ? <span style={{ color: 'var(--omega-ink-subtle)', fontWeight: 400 }}> · {l.title}</span> : null}
        </div>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {l.company}
        </div>
      </div>
      <span style={{ flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
        {fmtTime(l.discovered_at)}
      </span>
    </Link>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data: leadsData, isPending: leadsPending, isError: leadsError } = useLeads();
  const { data: metricsData } = useMetrics();

  const leads = leadsData?.leads ?? [];
  const counts: Record<string, number> = {};
  for (const l of leads) counts[l.status] = (counts[l.status] ?? 0) + 1;

  const needsAction = leads.filter(l => l.status === 'new');
  const recent = leads.slice(0, 8);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Sales' }]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Sales Brain
        </h1>
        {needsAction.length > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              background: '#7dd3fc',
              color: '#0a0d12',
              fontSize: '8pt',
              fontWeight: 700,
            }}
          >
            {needsAction.length}
          </span>
        )}
        <Link
          to="/omega/sales/leads"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4,
            color: '#7dd3fc',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
            letterSpacing: '0.04em',
          }}
        >
          All Leads
        </Link>
      </div>

      {/* Needs action banner */}
      {!leadsPending && !leadsError && <NeedsActionBanner leads={needsAction} />}

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total Leads" value={leadsData?.total ?? '—'} />
        <StatCard label="New" value={counts.new ?? 0} color="#7dd3fc" highlight={(counts.new ?? 0) > 0} />
        <StatCard label="Qualified" value={counts.qualified ?? 0} color="#6ee7b7" />
        <StatCard label="Won" value={counts.won ?? 0} color="#34d399" />
        <StatCard label="Lost" value={counts.lost ?? 0} color="#f87171" />
        {metricsData && (
          <StatCard
            label="Meetings Booked"
            value={metricsData.total_meetings_booked}
            color="#fbbf24"
          />
        )}
      </div>

      {/* Nav strip */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        {[
          { to: '/omega/sales/leads',     label: 'Leads' },
          { to: '/omega/sales/proposals', label: 'Proposals' },
          { to: '/omega/sales/pipeline',  label: 'Pipeline' },
        ].map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt',
              fontWeight: 600,
              padding: '6px 14px',
              textDecoration: 'none',
              letterSpacing: '0.04em',
            }}
          >
            {label}
          </Link>
        ))}
      </div>

      {/* Recent leads */}
      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 4 }}>
          Recent Leads
        </div>
        {leadsPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
        {leadsError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load leads.</div>}
        {!leadsPending && !leadsError && recent.length === 0 && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
            No leads yet — the pipeline will populate this when discovery runs.
          </div>
        )}
        {recent.map(l => <RecentRow key={l.id} l={l} />)}
      </section>
    </div>
  );
}
